import { FC, useMemo } from "react";

import {
  CreateGUFClosureRealisedPartTask,
  EditGUFClosureRealisedPartTask,
} from "@nens/brostar/uploadapi-client";

import { getApi } from "@/api/api-configuration";
import { getFormObjectValues, initializeFormEditingState } from "@/helpers/helpers";
import { useAfterFormSubmit } from "@/hooks/use-after-form-submit";
import { BrostarForm, useBrostarFormDataStorage } from "@/libs/brostar-form";
import { GufClosureRealisedPartFormProps } from "@/types/form-types";

import { getGufClosureRealisedPartSchema } from "../schema/guf-closure-realised-part-schema";
import { useGufClosureRealisedPartUiSchema } from "../schema/guf-closure-realised-part-ui-schema";
import { validateGufClosureRealisedPartForm } from "../schema/guf-validation";

const GufClosureRealisedPartForm: FC<GufClosureRealisedPartFormProps> = (props) => {
  const api = getApi();
  const afterSubmit = useAfterFormSubmit();

  const { oldUploadTask, task, type } = getFormObjectValues(props);
  const { isEditing, getIsEditing } = initializeFormEditingState(props);

  const { formData, handleChange } = useBrostarFormDataStorage<
    CreateGUFClosureRealisedPartTask | EditGUFClosureRealisedPartTask
  >({
    key: "guf-closure-realised-part",
    task,
  });

  const uiSchema = useGufClosureRealisedPartUiSchema(formData?.metadata.qualityRegime);

  const schema = useMemo(
    () => getGufClosureRealisedPartSchema({ type, isEditing }),
    [type, isEditing]
  );

  const onSubmit = async () => {
    if (!formData) return;
    if (getIsEditing(formData)) {
      await api.editGUFClosureRealisedPart(formData);
    } else {
      await api.createGUFClosureRealisedPart(formData);
    }
    if (oldUploadTask?.uuid) api.apiUploadtasksDelete({ uuid: oldUploadTask.uuid });
    await afterSubmit();
  };

  return (
    <BrostarForm<CreateGUFClosureRealisedPartTask | EditGUFClosureRealisedPartTask>
      uploadTask={oldUploadTask}
      schema={schema}
      formData={formData}
      onChange={handleChange}
      uiSchema={uiSchema}
      onSubmit={onSubmit}
      customValidate={validateGufClosureRealisedPartForm}
    />
  );
};

export { GufClosureRealisedPartForm };
